<template>
  <HeaderBar />
  <section class="carvia-container">
    <div class="mb-4 grid items-center gap-4 md:grid-cols-3">
      <Input v-model="searchQuery" variant="ghost" :placeholder="t('search')" class="flex-1" />
      <Input
        v-model="licencePlateFinSearchQuery"
        variant="ghost"
        :placeholder="t('searchForLicencePlateFin')"
        class="flex-1"
      />
      <Select
        v-model="stationId"
        variant="ghost"
        class="col-start-1"
        :options="stationOptions"
        :placeholder="t('station')"
      />
      <Select
        v-model="decommissionedFilter"
        variant="ghost"
        :options="decommissionedOptions"
        empty-value-to="null"
      />
    </div>

    <div v-if="vehicleTypes && !licencePlateFinSearchQuery" class="flex flex-col space-y-2">
      <VehicleTypeCard
        v-for="vehicleType in vehicleTypes"
        :key="vehicleType.id"
        :vehicle-type="vehicleType"
        hide-dropdown-dotted
      />
    </div>
    <div v-if="licencePlateFinSearchQuery" class="flex flex-col space-y-2">
      <CarCard
        v-for="car in cars"
        :key="car.id"
        :car="car"
        :vehicle-type="car.vehicleType"
        cannot-edit
      />
    </div>

    <div v-if="isLoading" class="mt-10 flex items-center justify-center">
      <Spinner class="text-hazel-600" />
    </div>

    <div v-if="isError">
      <p>{{ t('somethingWentWrong') }}</p>
    </div>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Input from '@/components/Input.vue';
import type { FindAllVehicleTypesQuery } from '@/entities/vehicle-type.entity';
import { useI18n } from 'vue-i18n';
import { useVehicleTypesWithQuery } from '@/queries/use-vehicle-types';
import { useUiStore } from '@/stores/ui.store';
import { computed, reactive, ref, watch, watchEffect } from 'vue';
import VehicleTypeCard from './VehicleTypeCard.vue';
import Select from '@/components/Select.vue';
import { useStations } from '@/queries/use-stations';
import { DecommissionedFilter } from '@/entities/cars/decommissioned-filter.enum';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import CarCard from './CarCard.vue';
import { useCarsWithQuery } from '@/queries/use-cars';
import type { FindAllCarsQueryDto } from '@/entities/cars/car.entity';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { refDebounced } from '@vueuse/core';

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const stationId = useRouteQueryRef('stationId', 'all', 'string');

const decommissionedFilter = useRouteQueryRef<DecommissionedFilter | null>(
  'decommissioned',
  DecommissionedFilter.ACTIVE,
  'string',
  { nullQueryValue: 'ALL' },
);
const searchQuery = useRouteQueryRef('search', '', 'string');
const searchQueryDebounced = refDebounced(searchQuery, 500);
const licencePlateFinSearchQuery = useRouteQueryRef('licencePlateFin', '', 'string');
const licencePlateFinSearchQueryDebounced = refDebounced(licencePlateFinSearchQuery, 500);

const query = computed<FindAllVehicleTypesQuery>(() => ({
  query: searchQueryDebounced.value,
  stationIds: stationId.value !== 'all' ? [stationId.value] : undefined,
  decommissioned: decommissionedFilter.value ?? undefined,
  isGhostCar: false,
  includeNumCars: true,
}));
const {
  data: vehicleTypes,
  isLoading: isLoadingVehicleTypes,
  isError: isVehicleTypesError,
} = useVehicleTypesWithQuery(
  query,
  computed(() => !licencePlateFinSearchQuery.value),
);

const carsQuery = computed<FindAllCarsQueryDto>(() => ({
  licencePlateFinQuery: licencePlateFinSearchQueryDebounced.value,
  stationIds: stationId.value !== 'all' ? [stationId.value] : undefined,
  decommissioned: decommissionedFilter.value ?? undefined,
  isGhostCar: false,
  limit: 20,
}));
const {
  data: carsData,
  isLoading: isLoadingCars,
  isError: isCarsError,
} = useCarsWithQuery(
  carsQuery,
  computed(() => !!licencePlateFinSearchQuery.value),
);
const cars = useFlattenPaginatedData(carsData);

const isLoading = computed(
  () =>
    (!licencePlateFinSearchQuery.value && isLoadingVehicleTypes.value) ||
    (licencePlateFinSearchQuery.value && isLoadingCars.value),
);

const isError = computed(
  () =>
    (!licencePlateFinSearchQuery.value && isVehicleTypesError.value) ||
    (licencePlateFinSearchQuery.value && isCarsError.value),
);

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const decommissionedOptions = computed(() => [
  {
    label: t('showAllCars'),
    value: null,
  },
  {
    label: t('hideDecommissionedCars'),
    value: DecommissionedFilter.ACTIVE,
  },
  {
    label: t('hideActiveCars'),
    value: DecommissionedFilter.DECOMMISSIONED,
  },
]);

const stationOptions = computed(() => {
  if (!stations.value) return [];
  return [
    {
      label: t('allStations'),
      value: 'all',
    },
    ...stations.value.map((station) => {
      return {
        label: getLocalizedField(station.info),
        value: station.id,
      };
    }),
  ];
});

const uiStore = useUiStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('vehicleTypes'));
});
</script>

<i18n lang="json">
{
  "en": {
    "vehicleTypes": "Vehicle Types",
    "newVehicleType": "New Vehicle Type",
    "addVehicleType": "+ Add Vehicle Type",
    "search": "Search vehicle type",
    "station": "Station",
    "allStations": "All Stations",
    "somethingWentWrong": "Something went wrong",
    "showAllCars": "Show all cars",
    "hideDecommissionedCars": "Hide decommissioned cars",
    "hideActiveCars": "Hide active cars",
    "searchForLicencePlateFin": "Search car (plate/FIN/fleet no.)"
  },
  "de": {
    "vehicleTypes": "Vehicle Types",
    "newVehicleType": "Neue Fahrzeug Klasse",
    "addVehicleType": "+ Vehicle Type hinzufügen",
    "search": "Vehicle Type suchen",
    "station": "Station",
    "allStations": "Alle Stationen",
    "somethingWentWrong": "Etwas ist schief gelaufen",
    "showAllCars": "Alle Autos anzeigen",
    "hideDecommissionedCars": "Ausrangierte Autos ausblenden",
    "hideActiveCars": "Aktive Autos ausblenden",
    "searchForLicencePlateFin": "Auto suchen (Kennz./FIN/FP-Nr.)"
  },
  "es": {
    "vehicleTypes": "Tipos de vehículo",
    "newVehicleType": "Nuevo tipo de vehículo",
    "addVehicleType": "+ Añadir tipo de vehículo",
    "search": "Buscar tipo de vehículo",
    "station": "Estación",
    "allStations": "Todas las estaciones",
    "somethingWentWrong": "Algo ha salido mal",
    "showAllCars": "Mostrar todos los vehículos",
    "hideDecommissionedCars": "Ocultar vehículos dados de baja",
    "hideActiveCars": "Ocultar vehículos activos",
    "searchForLicencePlateFin": "Buscar vehículo (matr./VIN/n.º flota)"
  }
}
</i18n>
